import React, { useState } from 'react';
import { FiMapPin, FiInfo, FiStar } from 'react-icons/fi';
import { Shop } from '../../lib/supabase/api/shopService';
import IconWrapper from '../print-steps/IconWrapper';
import ShopDetailsModal from './ShopDetailsModal';

import blackDropIcon from '../../assets/icons/black_drop.png';
import colorsDropIcon from '../../assets/icons/colors_drop.png';

interface ShopCardProps {
  shop: Shop;
  isSelected: boolean;
  onSelect: (shopId: string) => void;
  paperType?: string; // Current paper type from print settings
  isCompatible?: boolean; // Whether shop supports user's settings
  incompatibilityReasons?: string[]; // Reasons for incompatibility
}

// Mock types for missing properties
type ShopWithUIData = Shop & {
  is_open?: boolean;
  queue_length?: number;
  estimated_wait?: number;
  rating?: number;
  review_count?: number;
};

const ShopCard: React.FC<ShopCardProps> = ({ 
  shop, 
  isSelected, 
  onSelect, 
  paperType = 'A4',
  isCompatible = true,
  incompatibilityReasons = []
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Cast to extended shop type
  const shopWithUIData = shop as ShopWithUIData;

  // Use custom shop image if available, otherwise default
  const getShopImageUrl = () => {
    if (shop.shop_image_path) {
      return `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/shop_images/${shop.shop_image_path}`;
    }
    return '/print-shop.jpg';
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the card's onClick from triggering
    setShowDetailsModal(true);
  };

  // Get pricing for the selected paper type from shop.pricing object
  // Now pricing is an array of tiers, we show the lowest tier ("Starting from")
  let bwCost = 2.5; // fallback
  let colorCost = 10.0; // fallback
  let isMultipleTiers = false;
  
  if (shop.pricing && shop.pricing[paperType]) {
    const tiers = shop.pricing[paperType];
    
    // Check if it's the new tier array format
    if (Array.isArray(tiers) && tiers.length > 0) {
      // Use the first tier (lowest page count) for "Starting from" pricing
      const lowestTier = tiers[0];
      bwCost = lowestTier.user_monochrome_cost;
      colorCost = lowestTier.user_color_cost;
      isMultipleTiers = tiers.length > 1;
      console.log(`ShopCard [${shop.name}] - ${paperType} tiered pricing:`, {
        paperType,
        bwCost,
        colorCost,
        tierCount: tiers.length,
        lowestTierRange: `${lowestTier.min_pages}-${lowestTier.max_pages || '∞'} pages`
      });
    } else {
      // Legacy single pricing format (backward compatibility)
      bwCost = (tiers as any).user_monochrome_cost;
      colorCost = (tiers as any).user_color_cost;
      console.log(`ShopCard [${shop.name}] - ${paperType} legacy pricing:`, {
        paperType,
        bwCost,
        colorCost,
        pricingObject: tiers
      });
    }
  } else {
    // Fallback to A4 default values from shop object
    bwCost = shop.monochrome_cost || 2.5;
    colorCost = shop.color_cost || 10.0;
    console.log(`ShopCard [${shop.name}] - Using fallback A4 pricing:`, {
      paperType,
      bwCost,
      colorCost,
      hasPricingObject: !!shop.pricing,
      availablePaperTypes: shop.pricing ? Object.keys(shop.pricing) : []
    });
  }
    
  // Shop status - mocked for the UI
  const getShopStatus = () => {
    // Mock logic based on shop name to simulate different statuses
    if (shop.name.toLowerCase().includes('closed')) return "Closed";
    if (shop.name.toLowerCase().includes('busy')) return "Busy";
    return "Available";
  };
  
  const shopStatus = getShopStatus();
  
  // Estimated wait time for busy shops (in minutes)
  const getWaitTime = () => {
    if (shopStatus === "Busy") {
      return 15;
    }
    return 0;
  };
  
  // Queue length - mocked for UI
  const queueLength = 5;
  
  // Rating and reviews - mocked for UI
  // const rating = 4.5;
  // const reviewCount = 127;
  
  // Status color
  const getStatusColor = () => {
    switch (shopStatus) {
      case "Available": return "bg-green-100 text-green-800";
      case "Busy": return "bg-yellow-100 text-yellow-800";
      case "Closed": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };
  
  // Status indicator dot color
  const getStatusDotColor = () => {
    switch (shopStatus) {
      case "Available": return "bg-green-500";
      case "Busy": return "bg-yellow-500";
      case "Closed": return "bg-red-500";
      default: return "bg-gray-400";
    }
  };
  return (
    <>
      <div
        onClick={() => isCompatible && onSelect(shop.id)}
        className={`border ${
          !isCompatible 
            ? 'border-gray-200 bg-gray-50 opacity-60' 
            : isSelected 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200 hover:border-blue-200'
        } rounded-lg p-3.5 sm:p-4.5 mb-4 sm:mb-5 ${isCompatible ? 'cursor-pointer hover:bg-blue-50' : 'cursor-not-allowed'} flex flex-col sm:flex-row sm:items-center gap-4 transition-all shadow-sm ${isCompatible ? 'hover:shadow' : ''} relative`}
      >
        {/* Incompatibility banner */}
        {!isCompatible && incompatibilityReasons.length > 0 && (
          <div className="absolute top-2.5 left-2.5 right-2.5 bg-orange-50 border border-orange-200 rounded px-2 py-1 text-[10px] text-orange-800 font-medium z-10">
            Not available: {incompatibilityReasons.join(', ')}
          </div>
        )}
        
        {/* Status indicator positioned at top right */}
        <div className={`absolute ${!isCompatible && incompatibilityReasons.length > 0 ? 'top-10' : 'top-2.5'} right-2.5`}>
          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor()}`}>
            {shopStatus}
          </span>
        </div>
        
        {/* Mobile layout - restructured */}
        <div className="sm:hidden flex flex-col w-full relative">
          {/* Selection indicator - absolutely positioned on the right side */}
          <div className="absolute right-0 top-0 bottom-0 flex items-center h-full">
            <div className={`w-4 h-4 rounded-full border-2 ${
              isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
            } transition-all`}></div>
          </div>

          {/* Shop info with right padding to make space for the selection indicator */}
          <div className="flex w-full items-center mt-1 pr-6">
            <div className="flex-shrink-0 relative">
              <img
                src={getShopImageUrl()}
                alt={shop.name}
                className="w-14 h-14 rounded-md object-cover shadow-sm"
                loading="lazy"
              />
              {/* Status indicator dot (top right) */}
              <div className={`absolute -top-1 -right-1 w-3 h-3 ${getStatusDotColor()} rounded-full border-2 border-white shadow-sm`}></div>
              
              {/* Distance indicator - fixed positioning for mobile */}
              <div className="absolute -bottom-2 -left-1 bg-blue-600 text-white text-[9px] px-1.5 py-0.5 rounded-full shadow-sm font-medium flex items-center">
                {(shop.distance || 0.8).toFixed(1)} km
              </div>
            </div>

            <div className="flex-grow min-w-0 ml-3">
              {/* Shop name on its own line - no truncation */}
              <h3 className="font-semibold text-gray-800 text-sm">{shop.name}</h3>
              
              {/* Location with FiMapPin in IconWrapper - Mobile */}
              <div className="flex items-center mt-1.5 text-gray-500 text-[10px] truncate">
                <IconWrapper icon={FiMapPin} className="w-2.5 h-2.5 mr-1" />
                <span className="font-medium ">{shop.institution}</span> {shop.location}
                
                {/* Info button moved right after the location - increased gap */}
                <button
                  onClick={handleInfoClick}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-0.5 rounded-full transition-colors border border-blue-100 ml-3"
                  aria-label="Show shop details"
                >
                  <IconWrapper icon={FiInfo} className="h-2.5 w-2.5" />
                </button>
              </div>
              
              {/* Busy queue info */}
              {shopStatus === "Busy" && (
                <p className="text-[10px] text-gray-600 mt-1.5">
                  {queueLength} people in queue
                </p>
              )}
            </div>
          </div>

          {/* Cost indicators below shop name for mobile */}
          <div className="flex justify-between items-center mt-3 px-1 w-full pr-6">
            {/* Cost indicators for mobile */}
            <div className="flex flex-col space-y-1">
              {isMultipleTiers && (
                <span className="text-[9px] text-gray-500 font-medium">Starting from:</span>
              )}
              <div className="flex flex-row items-center space-x-6 max-[390px]:space-x-3">
                {/* BW Pricing with horizontal layout */}
                <div className="flex flex-row items-center">
                  <img
                    src={blackDropIcon}
                    alt="Black & White"
                    className="w-5 h-5 max-[390px]:w-4 max-[390px]:h-4"
                  />
                  <span className="font-medium text-xs ml-1.5 max-[390px]:text-[10px] max-[390px]:ml-1">₹{bwCost.toFixed(2)} / page</span>
                </div>

                {/* Color Pricing with horizontal layout */}
                <div className="flex flex-row items-center">
                  <img
                    src={colorsDropIcon}
                    alt="Color"
                    className="w-5 h-5 max-[390px]:w-4 max-[390px]:h-4"
                  />
                  <span className="font-medium text-xs ml-1.5 max-[390px]:text-[10px] max-[390px]:ml-1">₹{colorCost.toFixed(2)} / page</span> 
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop layout - horizontal row */}
        <div className="hidden sm:flex sm:items-center sm:w-full mt-1">
          <div className="flex-shrink-0 relative">
            <img
              src={getShopImageUrl()}
              alt={shop.name}
              className="w-16 h-16 rounded-lg object-cover shadow-sm"
              loading="lazy"
            />
            {/* Status indicator dot (top right) */}
            <div className={`absolute -top-1 -right-1 w-3.5 h-3.5 ${getStatusDotColor()} rounded-full border-2 border-white shadow-sm`}></div>
            
            {/* Distance indicator - fixed positioning for desktop */}
            <div className="absolute -bottom-2 -left-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full shadow-sm font-medium flex items-center">
              {(shop.distance || 0.8).toFixed(1)} km
            </div>
          </div>

          <div className="flex-grow min-w-0 ml-4">
            <div className="flex items-center">
              <h3 className="font-semibold text-gray-800 text-lg truncate">{shop.name}</h3>
            </div>
            
            {/* Location with FiMapPin in IconWrapper - Desktop */}
            <div className="flex items-center mt-2 text-gray-500 text-sm truncate">
              <IconWrapper icon={FiMapPin} className="w-3.5 h-3.5 mr-1" />
              <span className="font-normal">{shop.institution}</span> {shop.location}
              
              {/* Info button moved right after the location - increased gap */}
              <button
                onClick={handleInfoClick}
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-1 rounded-full transition-colors border border-blue-100 ml-3"
                aria-label="Show shop details"
              >
                <IconWrapper icon={FiInfo} className="h-3.5 w-3.5" />
              </button>
            </div>
            
            {/* <div className="flex items-center mt-2">
              <IconWrapper icon={FiStar} className="text-yellow-400 w-4 h-4 fill-current" />
              <span className="text-sm font-medium ml-1">{rating.toFixed(1)}</span>
              <span className="text-sm text-gray-500 ml-1">({reviewCount} reviews)</span>
              {shopStatus === "Busy" && (
                <span className="text-sm text-gray-600 ml-3">
                  {queueLength} people in queue
                </span>
              )}
            </div> */}
          </div>

          {/* Cost indicators for desktop */}
          <div className="flex-shrink-0 flex flex-col ml-auto mr-3">
            {isMultipleTiers && (
              <span className="text-xs text-gray-500 font-medium mb-1 text-center">Starting from:</span>
            )}
            <div className="flex flex-row items-center space-x-4">
              {/* BW Pricing */}
              <div className="flex flex-col items-center">
                <img
                  src={blackDropIcon}
                  alt="Black & White"
                  className="w-5 h-5"
                />
                <span className="font-medium text-sm mt-1.5">₹{bwCost.toFixed(2)} / page</span>
              </div>

              {/* Color Pricing */}
              <div className="flex flex-col items-center">
                <img
                  src={colorsDropIcon}
                  alt="Color"
                  className="w-5 h-5"
                />
                <span className="font-medium text-sm mt-1.5">₹{colorCost.toFixed(2)} / page</span> 
              </div>
            </div>
          </div>

          {/* Selected indicator for desktop - centered vertically */}
          <div className="flex items-center ml-3">
            <div className={`w-4 h-4 rounded-full border-2 ${
              isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
            } transition-all`}></div>
          </div>
        </div>
      </div>

      {/* Shop Details Modal */}
      <ShopDetailsModal
        shop={shop}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      />
    </>
  );
};

export default ShopCard;
